import React, { useEffect, useMemo, useRef, useState } from 'react'
import { Button, Empty, Spin, message } from 'antd'
import { fetchOrganizationMembers } from '../api/organization'
import type { Member, MemberListFilters, MemberPage, MemberTableGroup, Organization } from '../types'
import { buildRoleChips, memberQueryKey, orgChipsForMember, resolveOrganizationMemberCount } from '../utils'
import MemberGroupHeader from './MemberTable/MemberGroupHeader'
import MemberRow from './MemberTable/MemberRow'
import MemberTableHead from './MemberTable/MemberTableHead'
import MemberTableScroll from './MemberTable/MemberTableScroll'

interface AllOrganizationsMembersProps {
  organizations: Organization[]
  search: string
  filters: MemberListFilters
  filterSelection: MemberListFilters
  onFiltersChange: (filters: MemberListFilters) => void
  refreshKey?: number
  onInvite: (organization: Organization) => void
  onAssign?: (member: Member, organization: Organization) => void
  onEdit?: (member: Member, organization: Organization) => void
  onDisable?: (member: Member, organization: Organization) => void
  onEnable?: (member: Member, organization: Organization) => void
  onResendInvite?: (member: Member, organization: Organization) => Promise<void>
  currentCustomerId?: string | null
  onUpgrade?: (organization: Organization) => void
}

const PAGE_SIZE = 10

const emptyPage = (): MemberPage => ({
  members: [],
  total: 0,
  page: 1,
  pageSize: PAGE_SIZE,
})

function buildGroup(
  org: Organization,
  page: MemberPage | undefined,
  organizations: Organization[],
): MemberTableGroup {
  const members = page?.members || []
  return {
    org,
    rows: members.map((member) => ({
      member,
      roleInOrg: member.roles[org.id],
      roleChips: buildRoleChips(org.id, member.roleLists?.[org.id] || [member.roles[org.id]]),
      orgChips: orgChipsForMember(member, organizations, org),
    })),
  }
}

const AllOrganizationsMembers: React.FC<AllOrganizationsMembersProps> = ({
  organizations,
  search,
  filters,
  filterSelection,
  onFiltersChange,
  refreshKey = 0,
  onInvite,
  onAssign,
  onEdit,
  onDisable,
  onEnable,
  onResendInvite,
  currentCustomerId,
  onUpgrade,
}) => {
  const [pages, setPages] = useState<Record<string, MemberPage>>({})
  const [loadingOrgs, setLoadingOrgs] = useState<Record<string, boolean>>({})
  const [loading, setLoading] = useState(true)
  const queryKey = memberQueryKey(search, filters)
  const queryKeyRef = useRef(queryKey)
  const pageGenerationRef = useRef(0)

  useEffect(() => {
    queryKeyRef.current = queryKey
  }, [queryKey])

  const organizationIdsKey = useMemo(
    () => organizations.map((org) => org.id).join('|'),
    [organizations],
  )
  const organizationsRef = useRef(organizations)
  organizationsRef.current = organizations

  useEffect(() => {
    let cancelled = false
    const generation = pageGenerationRef.current + 1
    pageGenerationRef.current = generation

    const loadInitialPages = async () => {
      const orgs = organizationsRef.current
      if (orgs.length === 0) {
        setPages({})
        setLoading(false)
        return
      }

      setLoading(true)
      setPages({})
      setLoadingOrgs(Object.fromEntries(orgs.map((org) => [org.id, true])))
      try {
        const results = await Promise.all(
          orgs.map(async (org) => {
            const page = await fetchOrganizationMembers({
              customerId: org.customerId,
              page: 1,
              pageSize: PAGE_SIZE,
              search,
              filters,
            })
            return [org.id, page] as const
          }),
        )
        if (cancelled || generation !== pageGenerationRef.current) return
        setPages(Object.fromEntries(results))
      } catch (error) {
        if (cancelled || generation !== pageGenerationRef.current) return
        message.error(error instanceof Error ? error.message : 'Failed to load users')
        setPages(Object.fromEntries(orgs.map((org) => [org.id, emptyPage()])))
      } finally {
        if (cancelled || generation !== pageGenerationRef.current) return
        setLoading(false)
        setLoadingOrgs({})
      }
    }

    loadInitialPages()
    return () => {
      cancelled = true
    }
  }, [filters, organizationIdsKey, queryKey, refreshKey, search])

  const groups = useMemo(() => {
    return organizations.map((org) => buildGroup(org, pages[org.id], organizations))
  }, [organizations, pages])

  const loadMore = async (org: Organization) => {
    const current = pages[org.id] || emptyPage()
    const nextPage = current.page + 1
    const requestSearch = search
    const requestFilters = filters
    const generation = pageGenerationRef.current
    setLoadingOrgs((prev) => ({ ...prev, [org.id]: true }))
    try {
      const page = await fetchOrganizationMembers({
        customerId: org.customerId,
        page: nextPage,
        pageSize: PAGE_SIZE,
        search: requestSearch,
        filters: requestFilters,
      })
      if (generation !== pageGenerationRef.current) return
      if (memberQueryKey(requestSearch, requestFilters) !== queryKeyRef.current) return
      setPages((prev) => ({
        ...prev,
        [org.id]: {
          ...page,
          members: [...(prev[org.id]?.members || []), ...page.members],
        },
      }))
    } catch (error) {
      message.error(error instanceof Error ? error.message : 'Failed to load more users')
    } finally {
      if (generation === pageGenerationRef.current) {
        setLoadingOrgs((prev) => ({ ...prev, [org.id]: false }))
      }
    }
  }

  if (organizations.length === 0) {
    return <Empty description="No organizations to manage" />
  }

  return (
    <div className="um-refactor__member-table">
      <div className="um-refactor__table-wrap">
        <MemberTableScroll
          header={(
            <MemberTableHead
              organizations={organizations}
              showOrganizationFilter
              filters={filterSelection}
              onFiltersChange={onFiltersChange}
            />
          )}
        >
          {loading && groups.every(({ rows }) => rows.length === 0) ? (
            <div className="um-refactor__table-state">
              <Spin />
            </div>
          ) : (
            <div className="um-refactor__table-body">
              {groups.map(({ org, rows }) => {
                const page = pages[org.id]
                const hasMore = !!page && rows.length < page.total
                return (
                  <div key={org.id}>
                    <MemberGroupHeader
                      org={org}
                      memberCount={resolveOrganizationMemberCount(org, page?.total || 0)}
                      currentCustomerId={currentCustomerId}
                      onInvite={onInvite}
                      onUpgrade={onUpgrade}
                    />
                    {rows.map((row, index) => (
                      <MemberRow
                        key={`${org.id}-${row.member.id || row.member.email}-${index}`}
                        row={row}
                        organization={org}
                        onAssign={onAssign}
                        onEdit={onEdit}
                        onDisable={onDisable}
                        onEnable={onEnable}
                        onResendInvite={onResendInvite}
                      />
                    ))}
                    {rows.length === 0 && (
                      <div className="um-refactor__empty-row">
                        <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No users found" />
                      </div>
                    )}
                    {hasMore && (
                      <div className="um-refactor__load-more">
                        <Button loading={!!loadingOrgs[org.id]} onClick={() => loadMore(org)}>
                          Load more
                        </Button>
                      </div>
                    )}
                  </div>
                )
              })}
            </div>
          )}
        </MemberTableScroll>
      </div>
      <div className="um-refactor__footer-note">
        Super Admin and Owner view: organizations are loaded independently, and each organization keeps its own permissions.
      </div>
    </div>
  )
}

export default AllOrganizationsMembers
